
import React from 'react';
import { FlowRender } from "../flow-render";
import { RadioButton } from "../prm/radio-button";
import { Text } from "../prm/text";
import { Name } from "../prm/name";
import {OccupationMenu } from "./occupation-menu";
import { sprintf } from "sprintf-js";
import { GreyBox } from "../../client/funcs/utils";
import {role_titles} from "../onboarding-utils";
import { StateOptionMenu } from '../prm/state-menu';
import {InvestmentAccountType} from "../proto/investment_accounts";

const HEADER_TEXT = "All your answers will be kept secure and confidential.";
const PREFIX_0 = "employment_0";
const PREFIX_1 = "employment_1";

class Employment extends FlowRender {

  constructor(index) {
    super(index, []);

    this.create_params(PREFIX_0);
    this.create_params(PREFIX_1);
    this.url = "ongoing_source";
  }

  init(componentState, appState, component) {
    super.init(componentState, appState, component);
    const num_roles = role_titles(component.getOnboardingData()).length;
    if (!component.isBaseAcct() && num_roles === 2) {
      // Disable the first group of parameters
      Object.values(this[PREFIX_0]).forEach(prm => {
        prm.setDisabled(componentState, true);
      })
    }
    if (appState.account_type === InvestmentAccountType.custodial_ira.id) {
      // Locking the employment status to employed and lock it
      this.employment_1.status.setValue(componentState, 0, true);
      this.employment_1.status.setDisabled(componentState, true);
    }
  }

  create_params = prefix => {
    this[prefix] = {};

    const status = new RadioButton(
      new Name(prefix + "_status"),
      [
        new Name("employed", "Employed"),
        new Name("self_employed", "Self Employed"),
        new Name("student", "Student"),
        new Name("homemaker", "Homemaker"),
        new Name("retired", "Retired"),
        new Name("unemployed", "Unemployed"),
      ]);
    status.onChangeCallback = this.onChange;
    this.prmList.push(status);
    this[prefix].status = status;

    const employer_name = new Text(
      new Name(prefix + "_employer_name", "Employer Name"), null, {
        path: "company.companyName"});
    employer_name.onAction = this.onTextEdit;
    this.prmList.push(employer_name);
    this[prefix].employer_name = employer_name;

    const employer_address = new Text(
      new Name(prefix + "_employer_address", "Employer Address"), null, {
        path: "address.streetAddress"});
    employer_address.onAction = this.onTextEdit;
    this.prmList.push(employer_address);
    this[prefix].employer_address = employer_address;
    
    const employer_city = new Text(
      new Name(prefix + "_employer_city", "City"), null, {
        path: "address.city"});
    employer_city.onAction = this.onTextEdit;
    this.prmList.push(employer_city);
    this[prefix].employer_city = employer_city;

    const employer_zip = new Text(
      new Name(prefix + "_zip", "Zip"), null, {
        path: "finance.accountTemplate",
        arg0: "#####"
      });
    employer_zip.onAction = this.onTextEdit;
    this.prmList.push(employer_zip);
    this[prefix].employer_zip = employer_zip;

    const employer_state = new StateOptionMenu(new Name(prefix + "_state", "State"));
    employer_state.fake = 1;
    this.prmList.push(employer_state)
    this[prefix].employer_state = employer_state;

    const occupation_menu = new OccupationMenu(new Name(prefix + "_occupation", "Occupation"));
    this.prmList.push(occupation_menu);
    this[prefix].occupation_menu = occupation_menu;

    const occupation_other = new Text(new Name(
      prefix + "_occupation_other"), null, { path: "company.companyName" });
    this.prmList.push(occupation_other);
    this[prefix].occupation_other = occupation_other;
  };

  onChange = (component, newState) => {
    const num_roles = role_titles(component.getOnboardingData()).length;
    if (component.isBaseAcct()) {
      this.onChangeEmp(0, component, newState);
    }
    if (num_roles === 2) {
      this.onChangeEmp(1, component, newState);
    }
  };

  setRandomValues(state, component) {
    super.setRandomValues(state);
    const num_roles = role_titles(component.getOnboardingData()).length;

    for(let i=0; i < num_roles; i++) {
      if (i === 0 && !component.isBaseAcct()) {
        continue;
      }
      const prefix = sprintf("employment_%d", i);
      const status = this[prefix].status;
      if (!status.getDisabled(state)) {
        status.setRandomSingleValue(state);
      }
      const do_employer = status.isItemClicked(0, state) || status.isItemClicked(1, state);
      
      if (do_employer) {
        this[prefix].occupation_menu.setRandomValue(state);
      }
    }
  }

  onChangeEmp = (role_index, component, newState) => {
    const params = role_index === 0 ? this.employment_0 : this.employment_1;
    if (!params.status.isClicked(newState)) {
      return;
    }
    const has_employer = this.hasEmployer(role_index, newState);
    params.employer_name.setDisabled(newState, !has_employer);
    params.employer_address.setDisabled(newState, !has_employer);
    params.occupation_menu.setDisabled(newState, !has_employer);
  };

  hasEmployer = (role_index, state) => {
    const params = role_index === 0 ? this.employment_0 : this.employment_1;
    const status_value = params.status.getValue(state, true);
    return status_value === 'employed' || status_value === 'self_employed';
  }

  /** Called after a text entry is finished editing. */
  onTextEdit = component => {
    const newState = Object.assign({}, component.state);
    this.onChange(component, newState);
    component.setState(newState);
  };

  check_prms = (component, index, prm_list) => {
    const role_id = sprintf("employment_%d", index);
    prm_list.push(this[role_id].status);
    if (this.hasEmployer(index, component.state)) {
      prm_list.push(this[role_id].employer_name);
      prm_list.push(this[role_id].employer_address);
      prm_list.push(this[role_id].employer_city);
      prm_list.push(this[role_id].employer_zip);

      const occ_raw = this[role_id].occupation_menu.getValue(component.state);
      if (occ_raw && occ_raw.id === "other") {
        prm_list.push(this[role_id].occupation_other);
      }
    }
  }

  canNavigateNext(component) {
    const prm_list = [];
    const highlight_rects = [];
    const num_roles = role_titles(component.getOnboardingData()).length;

    // ?
    if (!component.isBaseAcct() && num_roles === 2) {
      this.check_prms(component, 1, prm_list);
    } else {
      for(let i=0; i < num_roles; i++) {
        this.check_prms(component, i, prm_list);
      }
    }

    prm_list.forEach(
      prm => prm.isFilledElseHighlight(component.state, highlight_rects));

    if (highlight_rects.length) {
      component.setState({highlight_rects});
      return null;
    }

    return component.getRootUrl() + "/net_worth";
  }

  renderColumn = (component, index, role) => {

    const has_employer = this.hasEmployer(index, component.state)
    const column_style = {
      marginTop: "1rem",
    };
    const container_style = {
      marginRight: "1rem",
      marginBottom: "1rem",
    };
    const checkbox_style = {
      marginTop: "0.25rem",
    };
    const prm_group = sprintf("employment_%d", index);
    const all_elems = [];

    // Do employment status menu
    const status = this[prm_group].status;
    
    status.buttonList.forEach((name, index) => {
      const key = sprintf("%s_%d", name.id, index);
      all_elems.push((
        <div key={key} align={"left"} style={{ ...container_style, width: 'fit-content' }}>
          {/* { status.renderCheckbox(index, component, checkbox_style) }
          { status.renderLabel(index, component.state) } */}
          { status.renderRadioLabel(index, component, checkbox_style )}
        </div>
      ))
    });

    // Employer name
    const input_style = {
      width: "100%",
    };

    const menu_style = {
      width: "100%",
    };
    
    const employer_name = this[prm_group].employer_name;
    has_employer && all_elems.push((
     <div key={"employer_name"} align={"left"} style={container_style}>
        {employer_name.renderInputDiv(component, input_style, employer_name.name.label)}
      </div>
    ));

    // Employer Address
    const employer_address = this[prm_group].employer_address;
    has_employer && all_elems.push((
      <div key={"employer_address"} align={"left"} style={container_style}>
        {employer_address.renderInputDiv(component, input_style, employer_address.name.label)}
      </div>
    ));

    // Employer City
    const employer_city = this[prm_group].employer_city;
    has_employer && all_elems.push((
     <div key={"employer_city"} align={"left"} style={container_style}>
        {employer_city.renderInputDiv(component, input_style, employer_city.name.label)}
      </div>
    ));

    const employer_state = this[prm_group].employer_state;
    has_employer && all_elems.push((
      <div key={"employer_state"} align={"left"} style={container_style}>
        {employer_state.renderMenuDiv(component, input_style, "State")}
      </div>
    ));

    const employer_zip = this[prm_group].employer_zip;
    has_employer && all_elems.push((
      <div key={"employer_zip"} align={"left"} style={container_style}>
        {employer_zip.renderInputDiv(component, input_style, "Zip Code")}
      </div>
    ));


    // Occupation
    const occupation = this[prm_group].occupation_menu;
    has_employer && all_elems.push((
     <div key={"occupation"} align={"left"} style={container_style}>
        <span>{occupation.name.label}</span>
        { occupation.renderMenu(component, menu_style) }
      </div>
    ));

    const occ_raw = occupation.getValue(component.state);
    // Occupations from older TDA accounts have different ids
    if (occ_raw && occ_raw.id === "other") {
      const other = this[prm_group].occupation_other;
      all_elems.push((
        <div key={"other"} style={container_style}>
          { other.renderInputDiv(component, input_style, "Other Occupation") }
        </div>
      ))
    }

    return (
      <div style={column_style}>
        { role && <div align={"center"} style={{fontSize: "1.5rem"}}>{role}</div> }
        <GreyBox>
          { all_elems }
        </GreyBox>
      </div>
    );

  };

  render = component => {
    const onboarding = component.getOnboardingData();
    const titles = role_titles(onboarding);
    let columns;

    columns = titles.map((title, index) => {

      return (
        <div style={{width: "30%"}} key={index}>
          { this.renderColumn(component, index, titles.length === 2 ? title : null) }
        </div>
      )
    });

    if (columns.length === 2) {
      columns.splice(1, 0, (<div key={10} style={{width: "2rem"}}/>));
    }

    return (
      <div>
        { this.renderHighlightRects(component) }
        { this.renderTitle(component, "Employment", "50%", "952129729")}
        { this.renderProgress(component) }
        <section style={{paddingTop: "1rem"}}>
          <div align={"center"} style={{fontSize: "1.5rem"}}>
            {HEADER_TEXT}
          </div>
          <div style={{display: "flex", margin: "auto"}}>
            <div style={{flex: "1"}}/>
            {columns}
            <div style={{flex: "1"}}/>
          </div>
        </section>
        {this.renderNextButtonRow(component, true)}
      </div>
    );
  }
}

export {Employment};
